import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { imgUrl } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";

export default function ProyectoModal({ open, onClose, proyecto, onSaved }) {
  const editing = !!proyecto;
  const [form, setForm] = useState({ nombre: "", clave: "", ubicacion: "", descripcion: "", superficie_m2: "", modo: "macroproyecto" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nombre: proyecto?.nombre || "", clave: proyecto?.clave || "", ubicacion: proyecto?.ubicacion || "",
        descripcion: proyecto?.descripcion || "", superficie_m2: proyecto?.superficie_m2 ?? "",
        modo: proyecto?.modo || "macroproyecto",
      });
      setFile(null);
      setPreview(proyecto?.imagen ? imgUrl(proyecto.imagen) : null);
    }
  }, [open, proyecto]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("El nombre del proyecto es obligatorio");
    setSaving(true);
    try {
      const payload = { ...form, superficie_m2: form.superficie_m2 === "" ? null : Number(form.superficie_m2) };
      let pid;
      if (editing) {
        await api.patch(`/properties/${proyecto.id}`, payload);
        pid = proyecto.id;
      } else {
        const { data } = await api.post("/properties", payload);
        pid = data.id;
      }
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/properties/${pid}/imagen`, fd);
      }
      toast.success(editing ? "Proyecto actualizado" : "Proyecto creado");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="proyecto-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-[#002B49]">{editing ? "Editar proyecto" : "Nuevo proyecto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block cursor-pointer">
            <div className="relative h-40 rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center hover:border-[#00A3B5] transition-colors">
              {preview ? (
                <img src={preview} alt="Portada" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="text-center text-slate-400">
                  <ImagePlus className="mx-auto mb-1" size={22} />
                  <p className="text-xs">Foto de portada del proyecto (clic para subir)</p>
                </div>
              )}
              {preview && (
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded">Cambiar foto</span>
              )}
            </div>
            <input data-testid="proyecto-foto-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickFile} />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Nombre *</Label>
              <Input data-testid="proyecto-nombre-input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Residencial Vista Norte" required />
            </div>
            <div className="space-y-1.5">
              <Label>Clave</Label>
              <Input data-testid="proyecto-clave-input" value={form.clave} onChange={(e) => setForm({ ...form, clave: e.target.value })} placeholder="RVN" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Ubicación</Label>
              <Input data-testid="proyecto-ubicacion-input" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="Ciudad, Estado" />
            </div>
            <div className="space-y-1.5">
              <Label>Superficie (m²)</Label>
              <Input data-testid="proyecto-superficie-input" type="number" min="0" step="any" value={form.superficie_m2} onChange={(e) => setForm({ ...form, superficie_m2: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tipo de proyecto</Label>
            <Select value={form.modo} onValueChange={(v) => setForm({ ...form, modo: v })}>
              <SelectTrigger data-testid="proyecto-modo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="macroproyecto">Macroproyecto (varias UPEs / lotes)</SelectItem>
                <SelectItem value="lote_unico">Lote único (la permisología aplica directo al lote)</SelectItem>
              </SelectContent>
            </Select>
            {form.modo === "lote_unico" && (
              <p className="text-[11px] text-slate-400">Se creará automáticamente una UPE única del lote para capturar sus trámites y permisología.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <Textarea data-testid="proyecto-descripcion-input" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={3} placeholder="Describe brevemente el desarrollo..." />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button data-testid="proyecto-guardar-button" type="submit" disabled={saving} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear proyecto"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
